import React, { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { t, useLanguage } from "@/lib/i18n";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import MemberRequests from "./MemberRequests";
import FeedbackInbox from "./FeedbackInbox";
import SubscriptionAdmin from "./SubscriptionAdmin";
import AdvertisementAdmin from "./AdvertisementAdmin";
import UserAdmin from "./UserAdmin";
import CasteAdmin from "./CasteAdmin";
import GotraAdmin from "./GotraAdmin";
import MediaAdmin from "./MediaAdmin";
import TreeOrderAdmin from "./TreeOrderAdmin";
import HowToAdmin from "./HowToAdmin";
import StatisticsAdmin from "./StatisticsAdmin";
import TreeRequestSettings from "./TreeRequestSettings";

const features = [
  { id: "requests", label: "Review Requests", component: MemberRequests, props: { admin: true } },
  { id: "feedback", label: "Feedback inbox", component: FeedbackInbox, props: { admin: true } },
  { id: "subscriptions", label: "Manage subscriptions", component: SubscriptionAdmin },
  { id: "advertisements", label: "Manage advertisements", component: AdvertisementAdmin },
  { id: "users", label: "Manage Users", component: UserAdmin },
  { id: "castes", label: "Manage Castes", component: CasteAdmin },
  { id: "gotras", label: "Manage Gotras", component: GotraAdmin },
  { id: "tree-request-settings", label: "Tree Request Settings", component: TreeRequestSettings },
  { id: "media", label: "Upload Limits", component: MediaAdmin },
  { id: "tree-order", label: "Sibling Order", component: TreeOrderAdmin },
  { id: "how-to", label: "Manage how-to videos", component: HowToAdmin },
  { id: "statistics", label: "Statistics", component: StatisticsAdmin },
];

export default function Admin() {
  useLanguage();
  const { user } = useAuth();
  const location = useLocation(), navigate = useNavigate();
  const active = location.pathname === "/admin" || location.pathname === "/admin/"
    ? "requests" : location.pathname.slice("/admin/".length);
  const [visited, setVisited] = useState(() => new Set([active]));
  useEffect(() => { setVisited((previous) => previous.has(active) ? previous : new Set([...previous, active])); }, [active]);
  if (user?.role !== "admin") return <Navigate to="/app" replace />;
  if (!features.some((feature) => feature.id === active)) return <Navigate to="/admin" replace />;
  return <main className="min-h-screen bg-[#FAF7F2] p-4 sm:p-6">
    <div className="mx-auto max-w-6xl space-y-5">
      <header className="space-y-2">
        <Link to="/app" className="text-amber-800 underline">{t("Back to tree")}</Link>
        <h1 className="font-serif text-3xl">{t("Admin")}</h1>
      </header>
      <Tabs value={active} onValueChange={(value) => navigate(`/admin/${value}`)} activationMode="manual">
        <TabsList aria-label={t("Admin features")} className="flex h-auto w-full flex-wrap justify-start gap-1 rounded-xl border bg-white p-2">
          {features.map((feature) => <TabsTrigger key={feature.id} value={feature.id}
            className="whitespace-normal text-left data-[state=active]:bg-amber-100 data-[state=active]:text-amber-950">{t(feature.label)}</TabsTrigger>)}
        </TabsList>
        {features.filter((feature) => visited.has(feature.id) || active === feature.id).map(({ id, component: Feature, props }) =>
          <TabsContent key={id} value={id} forceMount hidden={active !== id} className="mt-5 data-[state=inactive]:hidden">
            <Feature {...props} embedded active={active === id} />
          </TabsContent>)}
      </Tabs>
    </div>
  </main>;
}
